import { Fragment, ReactNode, useContext, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { NavigationContext } from "../context/navigation";
import useButtonTracking from "../hooks/useButtonTracking";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode | ReactNode[];
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children }) => {
  const { activeRoute } = useContext(NavigationContext);
  const [closeImpression, closeClick] = useButtonTracking({
    text: "Close",
    placement: "right",
    targetpath: "#",
  });

  const style = useMemo(() => {
    if (activeRoute === "thanks") {
      return {
        backgroundSize: "cover",
        backgroundImage:
          "url(https://assets.bose.com/content/dam/emails/file-hosting/holiday-hint/2111/drop-a-hint_pop-up_bg.jpg)",
      };
    }
  }, [activeRoute]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        className="fixed inset-0 overflow-y-auto bg-black bg-opacity-75"
        onClose={() => {
          closeClick();
          onClose();
        }}
        style={{
          zIndex: 9999,
        }}
      >
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 z-0" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div
              className="inline-block w-full max-w-3xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl"
              style={{
                height: 600,
                ...style,
              }}
            >
              {children}
              <div {...closeImpression}>
                <button
                  className="absolute right-4 top-4"
                  onClick={() => {
                    closeClick();
                    onClose();
                  }}
                >
                  <XIcon className="text-gray-900 w-8 h-8" />
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
